<template>
  <li class="nav-item nav-search d-flex align-items-center">

    <!-- ── TRIGGER PILL ── -->
    <div class="search-trigger" @click="open">
      <feather-icon icon="SearchIcon" size="14" class="search-trigger__icon" />
      <span class="search-trigger__text">Buscar...</span>
      <kbd class="search-trigger__kbd">{{ isMac ? '⌘' : 'Ctrl' }} K</kbd>
    </div>

    <!-- ── OVERLAY BACKDROP ── -->
    <transition name="fade">
      <div v-if="isOpen" class="sp-backdrop" @click="close" />
    </transition>

    <!-- ── SPOTLIGHT PANEL ── -->
    <transition name="slide-down">
      <div v-if="isOpen" class="sp-panel">

        <!-- Search input -->
        <div class="sp-input-wrap">
          <feather-icon icon="SearchIcon" size="18" class="sp-input-icon" />
          <input
            ref="spInput"
            v-model="query"
            class="sp-input"
            placeholder="Buscar vídeo, lead ou chat..."
            autocomplete="off"
            @keydown.esc="close"
            @keydown.up.prevent="moveUp"
            @keydown.down.prevent="moveDown"
            @keydown.enter.prevent="selectActive"
          />
          <span v-if="loading" class="sp-spinner" />
          <button v-if="query" class="sp-clear" @click="query = ''">
            <feather-icon icon="XIcon" size="14" />
          </button>
          <kbd class="sp-esc-hint">esc</kbd>
        </div>

        <!-- Body: categories + results -->
        <div class="sp-body">

          <!-- Left: category sidebar -->
          <ul class="sp-cats">
            <li
              v-for="cat in categories"
              :key="cat.key"
              class="sp-cat-item"
              :class="{ 'sp-cat-item--active': activeCategory === cat.key }"
              @click="activeCategory = cat.key; activeIndex = -1"
            >
              <feather-icon :icon="cat.icon" size="15" class="sp-cat-icon" />
              {{ cat.label }}
              <span v-if="cat.key !== 'all'" class="sp-cat-count">
                {{ countFor(cat.key) }}
              </span>
            </li>
          </ul>

          <!-- Right: results or empty state -->
          <div class="sp-results">

            <!-- Empty / initial state -->
            <div v-if="!query" class="sp-empty">
              <feather-icon icon="ZapIcon" size="36" class="sp-empty-icon" />
              <p class="sp-empty-title">Busca Inteligente</p>
              <p class="sp-empty-sub">Pesquise por vídeos, leads e chats vinculados</p>
            </div>

            <!-- No results -->
            <div v-else-if="!loading && visibleResults.length === 0" class="sp-empty">
              <feather-icon icon="SearchIcon" size="36" class="sp-empty-icon sp-empty-icon--muted" />
              <p class="sp-empty-title">Nenhum resultado</p>
              <p class="sp-empty-sub">Tente outro termo</p>
            </div>

            <!-- Result groups -->
            <template v-else>
              <div
                v-for="group in groupedResults"
                :key="group.type"
                class="sp-group"
              >
                <div class="sp-group-header">
                  <feather-icon :icon="group.icon" size="12" class="sp-group-header__icon" />
                  {{ group.label }}
                </div>

                <div
                  v-for="(item, idx) in group.items"
                  :key="item._uid"
                  class="sp-result"
                  :class="{ 'sp-result--active': item._flatIdx === activeIndex }"
                  @mouseenter="activeIndex = item._flatIdx"
                  @mousedown.prevent="navigate(item)"
                >
                  <!-- Video item -->
                  <template v-if="item._type === 'video'">
                    <div class="sp-result__icon sp-result__icon--video">
                      <feather-icon icon="PlayCircleIcon" size="16" />
                    </div>
                    <div class="sp-result__body">
                      <span class="sp-result__name">{{ item.vsl_name }}</span>
                      <span class="sp-result__meta">{{ item.file_type || 'Vídeo' }} · {{ item.qtde_load || 0 }} views</span>
                    </div>
                    <feather-icon icon="ChevronRightIcon" size="14" class="sp-result__arrow" />
                  </template>

                  <!-- Lead item -->
                  <template v-else-if="item._type === 'lead'">
                    <div class="sp-result__icon sp-result__icon--lead">
                      <feather-icon icon="UserIcon" size="16" />
                    </div>
                    <div class="sp-result__body">
                      <span class="sp-result__name">{{ item.name || item.email || '—' }}</span>
                      <span class="sp-result__meta">{{ item.email || '' }}{{ item.phone ? ' · ' + item.phone : '' }}</span>
                    </div>
                    <feather-icon icon="ChevronRightIcon" size="14" class="sp-result__arrow" />
                  </template>

                  <!-- Chat item -->
                  <template v-else-if="item._type === 'chat'">
                    <div class="sp-result__icon sp-result__icon--chat">
                      <feather-icon icon="MessageSquareIcon" size="16" />
                    </div>
                    <div class="sp-result__body">
                      <span class="sp-result__name">{{ item.name || item.nameChat || '—' }}</span>
                      <span class="sp-result__meta">Chat · {{ item.status === 1 ? 'Ativo' : 'Inativo' }}</span>
                    </div>
                    <feather-icon icon="ChevronRightIcon" size="14" class="sp-result__arrow" />
                  </template>

                  <!-- Doc item -->
                  <template v-else-if="item._type === 'doc'">
                    <div class="sp-result__icon sp-result__icon--doc">
                      <feather-icon icon="BookOpenIcon" size="16" />
                    </div>
                    <div class="sp-result__body">
                      <span class="sp-result__name">{{ item.title }}</span>
                      <span class="sp-result__meta">{{ item.desc }}</span>
                    </div>
                    <feather-icon icon="ChevronRightIcon" size="14" class="sp-result__arrow" />
                  </template>
                </div>
              </div>
            </template>

          </div>
        </div>

        <!-- Footer hint -->
        <div class="sp-footer">
          <span><kbd>↑↓</kbd> navegar</span>
          <span><kbd>↵</kbd> selecionar</span>
          <span><kbd>esc</kbd> fechar</span>
        </div>

      </div>
    </transition>
  </li>
</template>

<script>
import { useApi } from '@/composables/useApi'

const api = useApi()

export default {
  name: 'SearchBar',
  data() {
    return {
      isOpen: false,
      query: '',
      loading: false,
      activeCategory: 'all',
      activeIndex: -1,
      videos: [],
      leads: [],
      chats: [],
      isMac: navigator.platform.toUpperCase().includes('MAC'),
      docs: [],
      categories: [
        { key: 'all',   label: 'Todas as categorias', icon: 'GridIcon' },
        { key: 'video', label: 'Vídeos',               icon: 'PlayCircleIcon' },
        { key: 'lead',  label: 'Leads',                icon: 'UsersIcon' },
        { key: 'chat',  label: 'Chats',                icon: 'MessageSquareIcon' },
        { key: 'doc',   label: 'Documentação',         icon: 'BookOpenIcon' },
      ],
      _searchTimer: null,
    }
  },

  computed: {
    userId() {
      const u = JSON.parse(localStorage.getItem('userData'))
      return u && u.id
    },

    filteredVideos() {
      if (!this.query) return this.videos.slice(0, 5)
      const q = this.query.toLowerCase()
      return this.videos.filter(v => (v.vsl_name || '').toLowerCase().includes(q)).slice(0, 8)
    },
    filteredLeads() {
      if (!this.query) return this.leads.slice(0, 5)
      const q = this.query.toLowerCase()
      return this.leads.filter(l => (
        (l.name || '').toLowerCase().includes(q)
        || (l.email || '').toLowerCase().includes(q)
        || (l.phone || '').toLowerCase().includes(q)
      )).slice(0, 8)
    },
    filteredChats() {
      if (!this.query) return this.chats.slice(0, 5)
      const q = this.query.toLowerCase()
      return this.chats.filter(c => (c.name || c.nameChat || '').toLowerCase().includes(q)).slice(0, 5)
    },
    filteredDocs() {
      if (!this.query) return this.docs.slice(0, 4)
      const q = this.query.toLowerCase()
      return this.docs.filter(d => d.title.toLowerCase().includes(q) || d.desc.toLowerCase().includes(q)).slice(0, 6)
    },

    groupedResults() {
      const cat = this.activeCategory
      const groups = []
      let flatIdx = 0

      const addGroup = (type, label, icon, items) => {
        if ((cat === 'all' || cat === type) && items.length) {
          groups.push({
            type, label, icon,
            items: items.map(item => ({
              ...item, _type: type, _uid: `${type}-${item.id}`, _flatIdx: flatIdx++,
            })),
          })
        } else {
          flatIdx += items.length
        }
      }

      addGroup('video', 'Vídeos',       'PlayCircleIcon',    this.filteredVideos)
      addGroup('lead',  'Leads',        'UsersIcon',         this.filteredLeads)
      addGroup('chat',  'Chats',        'MessageSquareIcon', this.filteredChats)
      addGroup('doc',   'Documentação', 'BookOpenIcon',      this.filteredDocs)
      return groups
    },

    visibleResults() {
      return this.groupedResults.flatMap(g => g.items)
    },
  },

  watch: {
    query() {
      this.activeIndex = -1
    },
  },

  mounted() {
    window.addEventListener('keydown', this.handleGlobalKey)
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.handleGlobalKey)
  },

  methods: {
    handleGlobalKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        this.isOpen ? this.close() : this.open()
      }
    },

    async open() {
      this.isOpen = true
      await this.$nextTick()
      if (this.$refs.spInput) this.$refs.spInput.focus()
      if (!this.videos.length && !this.leads.length) this.loadData()
    },

    close() {
      this.isOpen = false
      this.query = ''
      this.activeIndex = -1
      this.activeCategory = 'all'
    },

    async loadData() {
      this.loading = true
      try {
        const [vRes, lRes, cRes] = await Promise.all([
          api.get(`vslUserId/${this.userId}`).catch(() => ({ data: [] })),
          api.get(`leads/${this.userId}`).catch(() => ({ data: [] })),
          api.get(`chat/${this.userId}`).catch(() => ({ data: [] })),
        ])
        this.videos = Array.isArray(vRes.data) ? vRes.data : []
        this.leads  = Array.isArray(lRes.data) ? lRes.data : []
        this.chats  = Array.isArray(cRes.data) ? cRes.data : []
      } catch (e) {
        console.error('search load error', e)
      }
      // Documentation entries (static — always available)
      this.docs = [
        { id: 'd1', title: 'Como embedar o player', desc: 'Guia de instalacao do player no seu site', route: '/documentacao' },
        { id: 'd2', title: 'Configurar Chat Booster', desc: 'Passo a passo para ativar o chat de captura', route: '/documentacao' },
        { id: 'd3', title: 'Analytics e metricas', desc: 'Entenda suas visualizacoes, plays e engajamento', route: '/documentacao' },
        { id: 'd4', title: 'Mapa de Calor', desc: 'Como funciona o heatmap de cliques e scroll', route: '/app/heatmap' },
        { id: 'd5', title: 'Personalizar player', desc: 'Cores, botoes, thumbnails e autoplay', route: '/documentacao' },
        { id: 'd6', title: 'Integracoes', desc: 'Conectar com RD Station, Pipedrive e mais', route: '/chats' },
        { id: 'd7', title: 'Webinario ao vivo', desc: 'Transmissao simulada via YouTube', route: '/app/webinar-simulator' },
        { id: 'd8', title: 'Captura de Leads', desc: 'Fluxos de chat para gerar leads qualificados', route: '/leads' },
        { id: 'd9', title: 'Chamada Inteligente', desc: 'Call-to-action no momento certo do video', route: '/documentacao' },
        { id: 'd10', title: 'Gerenciar usuarios', desc: 'Adicionar, editar e remover usuarios da plataforma', route: '/apps/usuarios/lista' },
      ]
      this.loading = false
    },

    countFor(type) {
      if (type === 'video') return this.filteredVideos.length
      if (type === 'lead')  return this.filteredLeads.length
      if (type === 'chat')  return this.filteredChats.length
      if (type === 'doc')   return this.filteredDocs.length
      return 0
    },

    moveDown() {
      const max = this.visibleResults.length - 1
      this.activeIndex = this.activeIndex < max ? this.activeIndex + 1 : 0
    },
    moveUp() {
      const max = this.visibleResults.length - 1
      this.activeIndex = this.activeIndex > 0 ? this.activeIndex - 1 : max
    },

    selectActive() {
      const item = this.visibleResults.find(r => r._flatIdx === this.activeIndex)
      if (item) this.navigate(item)
    },

    navigate(item) {
      this.close()
      if (item._type === 'video') {
        // Go directly to video analytics/editor
        this.$router.push(`/player/${item.id}/analytics`).catch(() => {})
      } else if (item._type === 'lead') {
        // Go directly to lead detail
        this.$router.push(`/detalhelead/${item.id_conversa || item.id}`).catch(() => {})
      } else if (item._type === 'chat') {
        // Go directly to chat editor
        this.$router.push({ path: '/chats', query: { id_chat: item.id } }).catch(() => {})
      } else if (item._type === 'doc') {
        // Navigate to doc route
        this.$router.push(item.route).catch(() => {})
      }
    },
  },
}
</script>

<style scoped>
/* ── PALETA: navy #0D3349 | red #C12B2B | orange #E86A10 | gold #F5B935 | cream #EDE8C4 ── */

/* ── TRIGGER PILL ── */
.search-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--dk-bg-inset, #f5f7fa);
  border: 1.5px solid var(--dk-border, #e4e8ef);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.18s ease;
  min-width: 240px;
  user-select: none;
}
.search-trigger:hover {
  border-color: #E86A10;
  box-shadow: 0 0 0 3px rgba(232, 106, 16, 0.08);
  background: var(--dk-bg-card-hover, #f0f2f5);
}
.search-trigger__icon { color: var(--dk-text-muted, #8a94a6); flex-shrink: 0; }
.search-trigger:hover .search-trigger__icon { color: #E86A10; }
.search-trigger__text { font-size: 0.84rem; color: var(--dk-text-muted, #8a94a6); flex: 1; font-weight: 500; }
.search-trigger__kbd {
  font-size: 0.68rem;
  background: var(--dk-bg-segment, #edf0f3);
  border: 1px solid var(--dk-border, #dde3ea);
  border-radius: 5px;
  padding: 2px 7px;
  color: var(--dk-text-muted, #8a94a6);
  font-family: -apple-system, BlinkMacSystemFont, monospace;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
}

/* ── BACKDROP ── */
.sp-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(13, 51, 73, 0.65);
  z-index: 9998;
  backdrop-filter: blur(2px);
}

/* ── PANEL ── */
.sp-panel {
  position: fixed;
  top: 72px;
  left: 50%;
  transform: translateX(-50%);
  width: min(700px, 96vw);
  background: #0D3349;
  border: 1px solid rgba(232, 106, 16, 0.35);
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(13, 51, 73, 0.7), 0 0 0 1px rgba(232,106,16,0.15);
  z-index: 9999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 82vh;
}

/* ── INPUT ── */
.sp-input-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(245, 185, 53, 0.15);
  background: rgba(255,255,255,0.04);
  flex-shrink: 0;
}
.sp-input-icon { color: #F5B935; flex-shrink: 0; }
.sp-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.95rem;
  color: #EDE8C4;
  caret-color: #E86A10;
}
.sp-input::placeholder { color: rgba(237, 232, 196, 0.4); }
.sp-clear {
  background: none;
  border: none;
  color: rgba(237,232,196,0.5);
  cursor: pointer;
  padding: 0 4px;
  display: flex;
  align-items: center;
}
.sp-clear:hover { color: #EDE8C4; }
.sp-esc-hint {
  font-size: 0.68rem;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(245,185,53,0.2);
  border-radius: 4px;
  padding: 1px 6px;
  color: rgba(237,232,196,0.6);
  font-family: monospace;
}
.sp-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(232,106,16,0.25);
  border-top-color: #E86A10;
  border-radius: 50%;
  animation: sp-spin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes sp-spin { to { transform: rotate(360deg); } }

/* ── BODY ── */
.sp-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ── CATEGORIES ── */
.sp-cats {
  list-style: none;
  margin: 0;
  padding: 10px 0;
  width: 200px;
  flex-shrink: 0;
  border-right: 1px solid rgba(245,185,53,0.12);
  overflow-y: auto;
  background: rgba(0,0,0,0.12);
}
.sp-cat-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  font-size: 0.83rem;
  color: rgba(237,232,196,0.65);
  cursor: pointer;
  border-radius: 8px;
  margin: 2px 8px;
  transition: all 0.15s;
}
.sp-cat-item:hover { background: rgba(232,106,16,0.12); color: #F5B935; }
.sp-cat-item--active {
  background: #E86A10;
  color: #fff;
}
.sp-cat-item--active .sp-cat-icon { color: #fff; }
.sp-cat-icon { flex-shrink: 0; color: rgba(237,232,196,0.4); }
.sp-cat-count {
  margin-left: auto;
  font-size: 0.72rem;
  background: rgba(255,255,255,0.1);
  border-radius: 20px;
  padding: 1px 7px;
}
.sp-cat-item--active .sp-cat-count { background: rgba(255,255,255,0.25); }

/* ── RESULTS ── */
.sp-results {
  flex: 1;
  overflow-y: auto;
  padding: 10px 8px;
}
.sp-results::-webkit-scrollbar { width: 4px; }
.sp-results::-webkit-scrollbar-track { background: transparent; }
.sp-results::-webkit-scrollbar-thumb { background: rgba(245,185,53,0.2); border-radius: 4px; }

/* ── EMPTY STATE ── */
.sp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  height: 100%;
  min-height: 200px;
}
.sp-empty-icon { color: #F5B935; margin-bottom: 12px; opacity: 0.8; }
.sp-empty-icon--muted { color: rgba(237,232,196,0.25); }
.sp-empty-title { font-size: 0.95rem; font-weight: 600; color: #EDE8C4; margin: 0 0 6px; }
.sp-empty-sub { font-size: 0.8rem; color: rgba(237,232,196,0.45); margin: 0; }

/* ── GROUPS ── */
.sp-group { margin-bottom: 6px; }
.sp-group-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(245,185,53,0.6);
  padding: 6px 10px 4px;
}
.sp-group-header__icon { color: #F5B935; }

/* ── RESULT ITEM ── */
.sp-result {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;
}
.sp-result:hover, .sp-result--active {
  background: rgba(232, 106, 16, 0.14);
}
.sp-result__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
}
.sp-result__icon--video  { background: linear-gradient(135deg, #E86A10, #F5B935); }
.sp-result__icon--lead   { background: linear-gradient(135deg, #C12B2B, #E04040); }
.sp-result__icon--chat   { background: linear-gradient(135deg, #0D3349, #1A5070); border: 1px solid rgba(245,185,53,0.3); }
.sp-result__icon--doc    { background: linear-gradient(135deg, #8B5CF6, #A78BFA); }
.sp-result__body { flex: 1; min-width: 0; }
.sp-result__name {
  display: block;
  font-size: 0.87rem;
  font-weight: 500;
  color: #EDE8C4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sp-result__meta {
  display: block;
  font-size: 0.73rem;
  color: rgba(237,232,196,0.45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sp-result__arrow { color: rgba(237,232,196,0.25); flex-shrink: 0; }
.sp-result--active .sp-result__arrow { color: #F5B935; }

/* ── FOOTER ── */
.sp-footer {
  display: flex;
  gap: 16px;
  padding: 8px 16px;
  border-top: 1px solid rgba(245,185,53,0.12);
  background: rgba(0,0,0,0.15);
  flex-shrink: 0;
}
.sp-footer span {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.73rem;
  color: rgba(237,232,196,0.45);
}
.sp-footer kbd {
  font-size: 0.65rem;
  background: rgba(232,106,16,0.15);
  border: 1px solid rgba(232,106,16,0.3);
  border-radius: 4px;
  padding: 1px 5px;
  color: #F5B935;
  font-family: monospace;
}

/* ── TRANSITIONS ── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; }
.slide-down-enter-active { transition: all 0.22s cubic-bezier(0.4,0,0.2,1); }
.slide-down-leave-active { transition: all 0.16s ease; }
.slide-down-enter { opacity: 0; transform: translateX(-50%) translateY(-12px); }
.slide-down-leave-to { opacity: 0; transform: translateX(-50%) translateY(-8px); }
</style>
